import { useState, useEffect, useCallback } from 'react';
import {
  getKioskSession,
  isValidKioskSession,
  clearKioskSession,
  extendKioskSession,
  selectKioskBusiness,
  KioskSession
} from '@/lib/attendance/scanner-auth-utils';

interface UseKioskSessionReturn {
  session: KioskSession | null;
  isAuthenticated: boolean;
  hasSelectedBusiness: boolean;
  loading: boolean;
  userId: string | null;
  businessId: string | null;
  businessName: string | null;
  businesses: Array<{ id: string; name: string }>;
  logout: () => void;
  selectBusiness: (businessId: string, businessName: string) => boolean;
  refreshSession: () => boolean;
  checkSession: () => void;
}

/**
 * Hook for managing kiosk session state (owner-level)
 * Supports multi-business access with business selection
 */
export function useKioskSession(): UseKioskSessionReturn {
  const [session, setSession] = useState<KioskSession | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(() => {
    setLoading(true);
    const currentSession = getKioskSession();

    if (currentSession && isValidKioskSession()) {
      setSession(currentSession);
    } else {
      setSession(null);
    }

    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    clearKioskSession();
    setSession(null);
  }, []);

  const selectBusiness = useCallback((businessId: string, businessName: string): boolean => {
    const success = selectKioskBusiness(businessId, businessName);
    if (success) {
      checkSession();
    }
    return success;
  }, [checkSession]);

  const refreshSession = useCallback((): boolean => {
    const success = extendKioskSession();
    if (success) {
      checkSession();
    }
    return success;
  }, [checkSession]);

  // Initial session check
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Check session validity periodically (every 5 minutes)
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      if (!isValidKioskSession()) {
        setSession(null);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [session]);

  // Auto-refresh session 30 minutes before expiry
  useEffect(() => {
    if (!session) return;

    const timeUntilExpiry = session.expires_at - Date.now();
    const refreshTime = Math.max(timeUntilExpiry - (30 * 60 * 1000), 1000);

    if (refreshTime > 0) {
      const timeout = setTimeout(() => {
        refreshSession();
      }, refreshTime);

      return () => clearTimeout(timeout);
    }
  }, [session, refreshSession]);

  // Listen for storage changes (multi-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'taginn_kiosk_session') {
        checkSession();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkSession]);

  return {
    session,
    isAuthenticated: !!session,
    hasSelectedBusiness: !!session?.selected_business_id,
    loading,
    userId: session?.user_id || null,
    businessId: session?.selected_business_id || null,
    businessName: session?.selected_business_name || null,
    businesses: session?.businesses || [],
    logout,
    selectBusiness,
    refreshSession,
    checkSession,
  };
}
